/**
 * Faculty API client for Stage Door Access
 * Handles faculty profile, dashboard, assignments, availability, and payouts
 */


import { handleUnauthorized } from './auth';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

/**
 * Get the auth token from localStorage
 */
function getAuthToken(): string | null {
  try {
    const tokensJson = localStorage.getItem('sda_auth_tokens');
    if (!tokensJson) return null;
    const tokens = JSON.parse(tokensJson) as { access_token: string; expires_at: number };
    if (tokens.expires_at * 1000 < Date.now()) {
      return null;
    }
    return tokens.access_token;
  } catch {
    return null;
  }
}

/**
 * Make an authenticated request
 */
async function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const headers: HeadersInit = {
    ...options.headers,
    Authorization: `Bearer ${token}`,
  };

  // Don't set Content-Type for FormData (let browser set it with boundary)
  if (!(options.body instanceof FormData)) {
    (headers as Record<string, string>)['Content-Type'] = 'application/json';
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });
  handleUnauthorized(response);
  return response;
}

// =============================================================================
// Types
// =============================================================================

export type FacultyStatus = 'invited' | 'onboarding' | 'pending_approval' | 'active' | 'inactive';

export interface OnboardingProgress {
  headshot_uploaded: boolean;
  bio_completed: boolean;
  stripe_connected: boolean;
  shows_selected: boolean;
  // Note: W-9 tax document collection is handled by Stripe Connect Express
}

export interface FacultyProfile {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  bio: string | null;
  headshot_url: string | null;
  status: FacultyStatus;
  default_rate_cents: number;
  stripe_account_id: string | null;
  stripe_account_status: 'pending' | 'restricted' | 'enabled' | null;
  // Note: W-9 tax document collection is handled by Stripe Connect Express
  onboarding_progress: OnboardingProgress;
  created_at: string;
  updated_at: string;
  shows?: FacultyShow[];
}

export interface FacultyShow {
  id: string;
  name: string;
  logo_url: string | null;
  certified_at: string;
}

export interface UpdateFacultyProfileData {
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  bio?: string | null;
}

export interface FacultyDashboardData {
  profile: FacultyProfile;
  upcoming_classes: FacultyUpcomingClass[];
  pending_assignments: FacultyPendingAssignment[];
  availability_blocks: AvailabilityBlock[];
  stats: {
    total_classes: number;
    upcoming_classes: number;
    pending_responses: number;
    total_earnings_cents: number;
  };
}

export interface FacultyUpcomingClass {
  id: string;
  booking_id: string;
  show_name: string;
  show_logo_url: string | null;
  class_name: string;
  scheduled_date: string;
  /** Raw TIME value from DB (e.g. '10:00:00'). Render via formatTimeDisplay from @sda/shared. */
  start_time: string;
  end_time: string | null;
  status: string;
  studio_name: string;
  studio_address: string;
  studio_city: string;
  studio_state: string;
  student_count: number | null;
  student_level: string | null;
  special_requests: string | null;
}

export interface FacultyPendingAssignment {
  id: string;
  class_id: string;
  booking_id: string;
  show_name: string;
  show_logo_url: string | null;
  class_name: string;
  scheduled_date: string;
  /** Raw TIME value from DB (e.g. '10:00:00'). Render via formatTimeDisplay from @sda/shared. */
  start_time: string;
  end_time: string | null;
  studio_name: string;
  studio_city: string;
  studio_state: string;
  assigned_at: string;
  response_deadline: string;
  student_count: number | null;
  student_level: string | null;
}

export interface AvailabilityBlock {
  id: string;
  faculty_id: string;
  block_type: 'date' | 'range';
  start_date: string;
  end_date: string | null;
  reason: string | null;
  created_at: string;
}

export interface CreateAvailabilityBlockData {
  block_type: 'date' | 'range';
  start_date: string;
  end_date?: string;
  reason?: string;
}

export interface FacultyPayout {
  id: string;
  amount_cents: number;
  status: 'pending' | 'in_transit' | 'paid' | 'failed' | 'canceled';
  arrival_date: string;
  description: string;
  class_id: string | null;
  class_date: string | null;
  show_name: string | null;
  studio_name: string | null;
  created_at: string;
}

export interface PayoutsSummary {
  total_earnings_cents: number;
  pending_cents: number;
  paid_cents: number;
  payouts: FacultyPayout[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface InvitationData {
  valid: boolean;
  faculty?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  };
  error?: string;
}

export interface StripeConnectStatus {
  connected: boolean;
  account_id: string | null;
  account_status: 'pending' | 'restricted' | 'enabled' | null;
  details_submitted: boolean;
  payouts_enabled: boolean;
  charges_enabled: boolean;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Validate invitation token (public - no auth required)
 */
export async function validateInvitation(token: string): Promise<InvitationData> {
  const response = await fetch(`${API_BASE_URL}/api/faculty/invitation/${token}`);
  const result = await response.json();

  if (!response.ok) {
    return {
      valid: false,
      error: result.error?.message || 'Invalid invitation',
    };
  }

  return {
    valid: true,
    faculty: result.data.faculty,
  };
}

/**
 * Accept invitation and set password
 */
export async function acceptInvitation(
  token: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  const response = await fetch(`${API_BASE_URL}/api/faculty/invitation/${token}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  const result = await response.json();

  if (!response.ok) {
    return {
      success: false,
      error: result.error?.message || 'Failed to accept invitation',
    };
  }

  return { success: true };
}

/**
 * Get current faculty profile
 */
export async function getFacultyProfile(): Promise<FacultyProfile> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/faculty/me`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get faculty profile');
  }

  return result.data.faculty;
}

/**
 * Update faculty profile
 */
export async function updateFacultyProfile(
  data: UpdateFacultyProfileData
): Promise<FacultyProfile> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/faculty/me`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to update faculty profile');
  }

  return result.data.faculty;
}

/**
 * Upload faculty headshot
 */
export async function uploadHeadshot(file: File): Promise<{ headshot_url: string }> {
  const formData = new FormData();
  formData.append('headshot', file);

  const response = await authenticatedFetch(`${API_BASE_URL}/api/faculty/me/headshot`, {
    method: 'POST',
    body: formData,
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to upload headshot');
  }

  return result.data;
}

/**
 * Get faculty dashboard data
 */
export async function getFacultyDashboard(): Promise<FacultyDashboardData> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/faculty/me/dashboard`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get dashboard data');
  }

  return result.data;
}

/**
 * Respond to assignment (accept or decline)
 */
export async function respondToAssignment(
  classId: string,
  response: 'accept' | 'decline',
  declineReason?: string
): Promise<{ success: boolean }> {
  const fetchResponse = await authenticatedFetch(
    `${API_BASE_URL}/api/faculty/me/assignments/${classId}/respond`,
    {
      method: 'POST',
      body: JSON.stringify({
        response,
        decline_reason: declineReason,
      }),
    }
  );

  const result = await fetchResponse.json();

  if (!fetchResponse.ok) {
    throw new Error(result.error?.message || 'Failed to respond to assignment');
  }

  return { success: true };
}

/**
 * Get availability blocks
 */
export async function getAvailability(): Promise<AvailabilityBlock[]> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/faculty/me/availability`);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get availability');
  }

  // Backend returns { success: true, data: [...] } where data is the availability array directly
  return result.data;
}

/**
 * Add availability block
 */
export async function addAvailabilityBlock(
  data: CreateAvailabilityBlockData
): Promise<AvailabilityBlock> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/faculty/me/availability`, {
    method: 'PUT',
    body: JSON.stringify({ action: 'add', ...data }),
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to add availability block');
  }

  return result.data.block;
}

/**
 * Remove availability block
 */
export async function removeAvailabilityBlock(blockId: string): Promise<void> {
  const response = await authenticatedFetch(`${API_BASE_URL}/api/faculty/me/availability`, {
    method: 'PUT',
    body: JSON.stringify({ action: 'remove', block_id: blockId }),
  });

  if (!response.ok) {
    const result = await response.json();
    throw new Error(result.error?.message || 'Failed to remove availability block');
  }
}

/**
 * Get Stripe Connect onboarding URL
 */
export async function getStripeOnboardingUrl(): Promise<{ url: string }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/faculty/me/stripe-connect/onboard`
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get Stripe onboarding URL');
  }

  return result.data;
}

/**
 * Get Stripe Connect status
 */
export async function getStripeConnectStatus(): Promise<StripeConnectStatus> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/faculty/me/stripe-connect/status`
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get Stripe Connect status');
  }

  return result.data;
}

/**
 * Get Stripe Express dashboard URL
 */
export async function getStripeDashboardUrl(): Promise<{ url: string }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/faculty/me/stripe-connect/dashboard`
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get Stripe dashboard URL');
  }

  return result.data;
}

/**
 * Disconnect Stripe Connect account to allow connecting a new one
 */
export async function disconnectStripeAccount(): Promise<{ disconnected: boolean; message: string }> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}/api/faculty/me/stripe-connect/disconnect`,
    { method: 'POST' }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to disconnect Stripe account');
  }

  return result.data;
}

/**
 * Get payout history
 */
export async function getPayouts(params?: {
  page?: number;
  limit?: number;
}): Promise<PayoutsSummary> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const url = `${API_BASE_URL}/api/faculty/me/payouts${
    searchParams.toString() ? `?${searchParams}` : ''
  }`;
  const response = await authenticatedFetch(url);
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error?.message || 'Failed to get payouts');
  }

  return result.data;
}

// Note: W-9 tax document collection is handled automatically by Stripe Connect Express
// The markW9Submitted function has been removed as it's no longer needed

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Get faculty full name
 */
export function getFacultyFullName(faculty: FacultyProfile): string {
  return `${faculty.first_name} ${faculty.last_name}`;
}

/**
 * Format faculty status for display
 */
export function formatFacultyStatus(status: FacultyStatus): string {
  const statusMap: Record<FacultyStatus, string> = {
    invited: 'Invited',
    onboarding: 'Onboarding',
    pending_approval: 'Pending Approval',
    active: 'Active',
    inactive: 'Inactive',
  };
  return statusMap[status] || status;
}

/**
 * Get status badge color class
 */
export function getFacultyStatusColor(status: FacultyStatus): string {
  const colorMap: Record<FacultyStatus, string> = {
    invited: 'bg-warning-100 text-warning-700',
    onboarding: 'bg-primary-100 text-primary-700',
    pending_approval: 'bg-warning-100 text-warning-700',
    active: 'bg-success-100 text-success-700',
    inactive: 'bg-warm-100 text-warm-700',
  };
  return colorMap[status] || 'bg-warm-100 text-warm-700';
}

/**
 * Calculate onboarding completion percentage
 * Note: W-9 is now handled by Stripe Connect, so it's not a separate step
 */
export function getOnboardingProgress(progress: OnboardingProgress): number {
  const steps = [
    progress.headshot_uploaded,
    progress.bio_completed,
    progress.stripe_connected,  // Stripe also collects W-9 tax info
    progress.shows_selected,
  ];
  const completed = steps.filter(Boolean).length;
  return Math.round((completed / steps.length) * 100);
}

/**
 * Format payout status for display
 */
export function formatPayoutStatus(status: string): string {
  const statusMap: Record<string, string> = {
    pending: 'Pending',
    in_transit: 'In Transit',
    paid: 'Paid',
    failed: 'Failed',
    canceled: 'Canceled',
  };
  return statusMap[status] || status;
}

/**
 * Get payout status badge color class
 */
export function getPayoutStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    pending: 'bg-warning-100 text-warning-700',
    in_transit: 'bg-primary-100 text-primary-700',
    paid: 'bg-success-100 text-success-700',
    failed: 'bg-danger-100 text-danger-700',
    canceled: 'bg-warm-100 text-warm-700',
  };
  return colorMap[status] || 'bg-warm-100 text-warm-700';
}

/**
 * Format currency from cents
 */
export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

/**
 * Check if assignment response is overdue
 */
export function isAssignmentOverdue(deadline: string): boolean {
  return new Date(deadline) < new Date();
}

/**
 * Get hours remaining to respond to assignment
 */
export function getHoursRemaining(deadline: string): number {
  const deadlineDate = new Date(deadline);
  const now = new Date();
  const diffMs = deadlineDate.getTime() - now.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
}
